import { useEffect, useRef, useState } from 'react';
import {
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { useLeasedStream } from '@/hooks/lib/useStreamLease';
import { useNotificationActions, type NotificationStreamStatus } from '@/stores/notification.store';
import { getActiveTenantId } from '@/lib/activeTenant';
import { notificationKeys } from '@/lib/queryKeys';
import { customerVisitEventInvalidations } from '@/hooks/jobs/useJobAppointments';
import { internalCommunicationKeys } from '@/lib/internal-communications';
import { recoverSession, redirectToLogin } from '@/lib/api/session';
import type {
  NotificationCategory,
  NotificationPriority,
  NotificationStatusFilter,
  NotificationsListResponse,
  StaffNotification,
} from '@/types/notifications';

type NotificationEventType =
  | 'notification.created'
  | 'notification.read'
  | 'notification.archived'
  | 'notification.unread_count'
  | 'resync'
  | 'heartbeat';

interface NotificationStreamEvent {
  type: NotificationEventType;
  data: Record<string, unknown> | null;
}

const EVENTS_URL = '/api/notifications/stream';
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 30_000;
const JITTER_RATIO = 0.25;

function supportsStreamingFetch(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.fetch !== 'function') return false;
  if (typeof window.ReadableStream !== 'function') return false;
  if (typeof TextDecoder !== 'function') return false;
  return true;
}

function resolveOperationsBaseUrl(): string {
  const opsUrl = import.meta.env.VITE_OPERATIONS_URL ?? '';
  return typeof opsUrl === 'string' ? opsUrl.replace(/\/+$/, '') : '';
}

function invalidateForEvent(
  qc: QueryClient,
  evt: NotificationStreamEvent,
  actions: {
    setUnreadCount: (count: number) => void;
    incrementUnread: () => void;
    announce: (message: string) => void;
  },
): void {
  if (evt.type === 'heartbeat') return;
  if (evt.type === 'resync') {
    void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
    void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    return;
  }
  if (evt.type === 'notification.unread_count') {
    const count = readCount(evt.data);
    if (count !== null) actions.setUnreadCount(count);
    void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    return;
  }

  if (evt.type === 'notification.created') {
    const item = readNotificationItem(evt.data);
    if (item) {
      // BR-77 — a customer confirming, cancelling or asking to reschedule
      // happens in a public browser, outside this React Query client. The
      // office already gets the notification; ride it to refresh the job the
      // operator has open instead of polling every job page for the chance.
      customerVisitEventInvalidations(qc, item.type);
      if (item.type === 'internal_message.received') {
        void qc.invalidateQueries({ queryKey: internalCommunicationKeys.all });
      }
      // Operational groups are aggregated server-side. The realtime payload is
      // the concrete occurrence, so inserting it directly would temporarily
      // show a duplicate card beside the existing group.
      if (item.groupKey) {
        void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
        void qc.invalidateQueries({ queryKey: notificationKeys.groupItemsAll() });
      } else {
        upsertCreatedNotification(qc, item);
      }
      actions.announce(`New notification: ${item.title}`);
    } else {
      void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
    }
    const count = readCount(evt.data);
    if (count !== null) actions.setUnreadCount(count);
    else if (item && item.archivedAt === null && item.readAt === null) actions.incrementUnread();
    void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    return;
  }

  if (evt.type === 'notification.read' || evt.type === 'notification.archived') {
    void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
    void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
  }
}

type NotificationListData = InfiniteData<NotificationsListResponse, string | null>;

type NotificationListFilters = {
  unreadOnly?: boolean;
  category?: NotificationCategory | 'all';
  priority?: NotificationPriority | 'all';
  eventType?: string;
  excludeEventType?: string;
  status?: NotificationStatusFilter;
  limit?: number;
};

function upsertCreatedNotification(qc: QueryClient, item: StaffNotification): void {
  const queries = qc.getQueriesData<NotificationListData>({
    queryKey: notificationKeys.lists(),
  });

  for (const [queryKey, data] of queries) {
    if (!data?.pages.length) continue;
    const filters = readListFilters(queryKey);
    if (!matchesListFilters(item, filters)) continue;
    const limit = typeof filters.limit === 'number' && filters.limit > 0 ? filters.limit : null;
    const pages = data.pages.map((page) => ({
      ...page,
      items: page.items.filter(
        (existing) => existing.recipientId !== item.recipientId && existing.id !== item.id,
      ),
    }));
    const firstPage = pages[0];
    if (!firstPage) continue;
    pages[0] = {
      ...firstPage,
      items: [item, ...firstPage.items].slice(0, limit ?? undefined),
    };
    qc.setQueryData(queryKey, { ...data, pages });
  }
}

function readListFilters(queryKey: QueryKey): NotificationListFilters {
  const maybeFilters = queryKey[queryKey.length - 1];
  if (!maybeFilters || typeof maybeFilters !== 'object' || Array.isArray(maybeFilters)) {
    return {};
  }
  return maybeFilters as NotificationListFilters;
}

function matchesListFilters(item: StaffNotification, filters: NotificationListFilters): boolean {
  if (filters.category && filters.category !== 'all' && item.category !== filters.category) {
    return false;
  }
  if (filters.priority && filters.priority !== 'all' && item.priority !== filters.priority) {
    return false;
  }
  if (filters.eventType && item.type !== filters.eventType) return false;
  if (filters.excludeEventType && item.type === filters.excludeEventType) return false;

  const status = filters.status ?? (filters.unreadOnly ? 'unread' : 'all');
  const archived = item.archivedAt !== null;
  const unread = item.readAt === null && !archived;
  switch (status) {
    case 'all':
      return !archived;
    case 'unread':
      return unread;
    case 'read':
      return item.readAt !== null && !archived;
    case 'archived':
      return archived;
  }
}

function readNotificationItem(data: Record<string, unknown> | null): StaffNotification | null {
  if (!data) return null;
  const item = data['item'];
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const candidate = item as Partial<StaffNotification>;
  if (
    typeof candidate.id !== 'string' ||
    typeof candidate.recipientId !== 'string' ||
    typeof candidate.title !== 'string' ||
    typeof candidate.body !== 'string' ||
    typeof candidate.priority !== 'string' ||
    typeof candidate.category !== 'string'
  ) {
    return null;
  }
  return candidate as StaffNotification;
}

function readCount(data: Record<string, unknown> | null): number | null {
  if (!data) return null;
  const direct = data['count'];
  if (typeof direct === 'number') return direct;
  const unreadCount = data['unreadCount'];
  if (typeof unreadCount === 'number') return unreadCount;
  return null;
}

function parseSseBlock(block: string): NotificationStreamEvent | null {
  let type: string | null = null;
  const dataLines: string[] = [];

  for (const rawLine of block.split('\n')) {
    if (rawLine.startsWith(':')) continue;
    const colon = rawLine.indexOf(':');
    if (colon === -1) continue;
    const field = rawLine.slice(0, colon);
    const value = rawLine.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') type = value;
    else if (field === 'data') dataLines.push(value);
  }

  if (!type) return null;
  const dataStr = dataLines.join('\n');
  let data: Record<string, unknown> | null = null;
  if (dataStr.length > 0) {
    try {
      data = JSON.parse(dataStr) as Record<string, unknown>;
    } catch {
      data = null;
    }
  }
  return { type: type as NotificationEventType, data };
}

async function consumeStream(
  response: Response,
  onEvent: (evt: NotificationStreamEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const body = response.body;
  if (!body) throw new Error('SSE response has no body');

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      let sepIdx: number;
      while ((sepIdx = findEventBoundary(buffer)) !== -1) {
        const block = buffer.slice(0, sepIdx).replace(/\r\n/g, '\n');
        const boundaryLen = buffer.startsWith('\r\n\r\n', sepIdx) ? 4 : 2;
        buffer = buffer.slice(sepIdx + boundaryLen);
        if (block.trim().length === 0) continue;
        const evt = parseSseBlock(block);
        if (evt) onEvent(evt);
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

function findEventBoundary(buf: string): number {
  const lf = buf.indexOf('\n\n');
  const crlf = buf.indexOf('\r\n\r\n');
  if (lf === -1) return crlf;
  if (crlf === -1) return lf;
  return Math.min(lf, crlf);
}

function computeBackoffMs(attempt: number): number {
  const raw = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_CAP_MS);
  const jitter = raw * JITTER_RATIO * Math.random();
  return Math.round(raw - raw * JITTER_RATIO + jitter);
}

export function useNotificationStream(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const qc = useQueryClient();
  const [status, setStatus] = useState<NotificationStreamStatus>('idle');
  const [lastEventAt, setLastEventAt] = useState<Date | null>(null);
  const { setStreamStatus, setUnreadCount, incrementUnread, announce } = useNotificationActions();
  const actionRef = useRef({ setUnreadCount, incrementUnread, announce });
  const hasConnectedRef = useRef(false);
  actionRef.current = { setUnreadCount, incrementUnread, announce };

  const applyEvent = (evt: NotificationStreamEvent) => {
    setLastEventAt(new Date());
    invalidateForEvent(qc, evt, actionRef.current);
  };
  // One tab in the browser holds this connection; the rest are fed from it.
  // See `useStreamLease` for why three streams a tab does not fit.
  const { leader: live, publishEvent } = useLeasedStream<NotificationStreamEvent>({
    name: 'notifications',
    want: enabled,
    status,
    applyEvent,
    applyStatus: (next) => setStatus(next as NotificationStreamStatus),
  });

  useEffect(() => {
    setStreamStatus(status);
  }, [status, setStreamStatus]);

  useEffect(() => {
    if (!live) {
      setStatus('idle');
      return;
    }
    if (!supportsStreamingFetch()) {
      setStatus('error');
      return;
    }

    const abortController = new AbortController();
    let cancelled = false;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const fullUrl = `${resolveOperationsBaseUrl()}${EVENTS_URL}`;

    const safeSetStatus = (next: NotificationStreamStatus) => {
      if (!cancelled) setStatus(next);
    };

    async function connect(): Promise<void> {
      if (cancelled) return;
      safeSetStatus('connecting');
      const token = useAuthStore.getState().token;
      const tenantId = getActiveTenantId();
      if (!token) {
        safeSetStatus('closed');
        return;
      }

      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
      };
      if (tenantId) headers['x-tenant-id'] = tenantId;

      try {
        const response = await fetch(fullUrl, {
          method: 'GET',
          headers,
          signal: abortController.signal,
          credentials: 'omit',
          cache: 'no-store',
        });
        if (response.status === 401) {
          // Same rule as the inbox stream: retry on a newer token, else end.
          if (await recoverSession(token)) {
            scheduleReconnect();
            return;
          }
          safeSetStatus('closed');
          redirectToLogin();
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`Notifications SSE connect failed: ${response.status}`);
        }
        attempt = 0;
        if (hasConnectedRef.current) {
          void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
          void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
        }
        hasConnectedRef.current = true;
        safeSetStatus('open');
        await consumeStream(
          response,
          (evt) => {
            if (cancelled) return;
            setLastEventAt(new Date());
            invalidateForEvent(qc, evt, actionRef.current);
            publishEvent(evt);
          },
          abortController.signal,
        );
        if (!cancelled) {
          safeSetStatus('closed');
          scheduleReconnect();
        }
      } catch (err) {
        if (cancelled || abortController.signal.aborted) return;
        console.warn('[useNotificationStream] connection error', err);
        safeSetStatus('error');
        scheduleReconnect();
      }
    }

    function scheduleReconnect(): void {
      if (cancelled) return;
      const delay = computeBackoffMs(attempt);
      attempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, delay);
    }

    void connect();

    return () => {
      cancelled = true;
      abortController.abort();
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
    };
  }, [live, qc, setStreamStatus, publishEvent]);

  return { status, lastEventAt };
}

export type { StaffNotification };
